/**
 * Side panel for the Trace Explorer.
 *
 * Shows the currently selected node's metadata + a deep-link back to
 * the entity's "native" admin page (Messages / Workflow runs /
 * Deliveries / Ingress). That cross-link is the whole point of the
 * Trace view: once an operator spots the suspect node in the graph
 * they jump straight to the row where they can drill deeper.
 */
import { ArrowUpRight, MousePointer2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/page";
import type { TraceNode, TraceNodeKind } from "@/lib/api-trace";


/** Map a trace node back to the admin URL that lets the operator
 *  drill deeper, plus the i18n key for the button label.
 *
 *  Returns ``null`` when no equivalent page exists for the kind.
 */
function entityLink(
  node: TraceNode,
): { to: string; labelKey: string } | null {
  const data = node.data as Record<string, string | undefined>;
  switch (node.kind) {
    case "message": {
      const id = data.message_id;
      return id
        ? { to: `/messages?message_id=${id}`, labelKey: "trace.sidebar.openIn.messages" }
        : null;
    }
    case "execution": {
      const id = data.execution_id;
      return id
        ? { to: `/workflow-runs?execution_id=${id}`, labelKey: "trace.sidebar.openIn.runs" }
        : null;
    }
    case "delivery": {
      const id = data.delivery_id;
      return id
        ? { to: `/deliveries?delivery_id=${id}`, labelKey: "trace.sidebar.openIn.deliveries" }
        : null;
    }
    case "ingress_source": {
      const id = data.ingress_source_id ?? data.id;
      return id
        ? { to: `/ingress?focus=${id}`, labelKey: "trace.sidebar.openIn.ingress" }
        : null;
    }
    case "node_execution":
      // Node executions are surfaced inside the execution drawer;
      // route through the parent execution so the drawer can scroll
      // to the node row.
      if (data.execution_id) {
        return {
          to: `/workflow-runs?execution_id=${data.execution_id}`,
          labelKey: "trace.sidebar.openIn.parent",
        };
      }
      return null;
    default:
      return null;
  }
}


function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  // Compact JSON for object/array values — keeps the sidebar readable
  // without a full prettifier (and we already render strings inline).
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}


export interface TraceSidebarProps {
  node: TraceNode | null;
  onTrace?: (anchor: { type: string; id: string }) => void;
}

export function TraceSidebar({ node, onTrace }: TraceSidebarProps) {
  const { t } = useTranslation();
  // i18n keys for kind labels live under ``trace.kind.<kind>`` — keep
  // them in sync with the TraceNodeKind union in api-trace.ts.
  const kindLabel = (kind: TraceNodeKind): string => t(`trace.kind.${kind}`);

  if (!node) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState
          icon={MousePointer2}
          title={t("trace.sidebar.selectTitle")}
          description={t("trace.sidebar.selectDescription")}
        />
      </div>
    );
  }

  const link = entityLink(node);
  // Re-anchor: pivot the trace to this node so the operator can keep
  // exploring outwards from it. Only applies to anchor-capable kinds.
  const anchorType =
    node.kind === "message" || node.kind === "execution" ||
    node.kind === "node_execution" || node.kind === "delivery"
      ? node.kind
      : null;
  const entries = Object.entries(node.data ?? {});

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-5">
      <div>
        <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {kindLabel(node.kind)}
        </div>
        <div className="mt-1 text-base font-semibold text-foreground break-words">
          {node.label}
        </div>
      </div>

      {(link || (onTrace && anchorType && (node.data as Record<string, string>)[`${anchorType}_id`])) && (
        <div className="flex flex-wrap gap-2">
          {link && (
            <Button asChild size="sm" variant="outline">
              <Link to={link.to}>
                {t(link.labelKey)}
                <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" />
              </Link>
            </Button>
          )}
          {onTrace && anchorType && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                const id = (node.data as Record<string, string>)[`${anchorType}_id`];
                if (id) onTrace({ type: anchorType, id });
              }}
            >
              {t("trace.sidebar.reanchor")}
            </Button>
          )}
        </div>
      )}

      <div className="rounded-md border border-border bg-surface/50">
        <div className="border-b border-border px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {t("trace.sidebar.details")}
        </div>
        <dl className="divide-y divide-border text-xs">
          {entries.length === 0 && (
            <div className="px-3 py-2 text-muted-foreground">
              {t("trace.sidebar.noMetadata")}
            </div>
          )}
          {entries.map(([k, v]) => (
            <div key={k} className="grid grid-cols-[110px_1fr] gap-3 px-3 py-2">
              <dt className="font-mono text-muted-foreground">{k}</dt>
              <dd className="break-all font-mono text-foreground">{formatValue(v)}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="text-[10px] text-muted-foreground">
        {t("trace.sidebar.nodeId")}:{" "}
        <span className="font-mono">{node.id}</span>
      </div>
    </div>
  );
}
